import { useEffect, useState } from "react";
import { ColumnsType } from "antd/es/table";
import { cellWidth } from "../../../core/methods/tableCellWidth";
import Datatable from "../../../core/common/dataTable";
import FiltersTrackCase, {
  FiltersSelectType,
  SearchFormDataType,
  SearchPayloadType,
} from "../FiltersTrackCase";
import {
  parseDate,
  parseDaysString,
  parseNumber,
} from "../../../core/methods/sorterMethods";
import TimelineModal from "../TimelineModal";
import ModifyInviteModal from "./ModifyInviteModal";
import { loggedinList, statusColors } from "../ApplicantInvited";
import { getSearchableHeader } from "../../utils/getSearchableHeader";
import useApi from "../../../core/customhook/useApi";
import webservice_url from "../../../core/webservice/Webservice_url";
import Loading from "../../../core/common/custom-loading/Loading";
import EditIcon from "../../../core/svg-icons/EditIcon";
import Swal from "sweetalert2";
import { exportAntdTableToExcel } from "../../utils/exportExcel";
import dayjs from "dayjs";
import { filterByColumnSearch } from "../../utils/filterByColumnSearch";

export type TableApiPayload = {
  program_id: string[];
  department_id: string[];
  status: string[];
  assigned_user_id: string[];
  project_id: string[];
  page: number;
  limit: number;
};

const buildTableApiPayload = (
  searchPayload: Partial<SearchPayloadType>,
  page: number,
  limit: number,
  statusId?: string,
): TableApiPayload => ({
  program_id: searchPayload.program ?? [],
  department_id: searchPayload.department ?? [],
  status: statusId ? [statusId] : [],
  project_id: searchPayload.project ?? [],
  assigned_user_id: searchPayload.users ?? [],
  page,
  limit,
});

interface ApplicantLoggedInProps {
  statusId?: string;
}

const ApplicantLoggedIn = ({ statusId }: ApplicantLoggedInProps) => {
  const [searchFilter, setSearchFilter] = useState<SearchFormDataType>({
    case_id: "",
    applicant_name: "",
    email: "",
    from_date: "",
    to_date: "",
  });
  const [filterData, setFilterData] = useState<FiltersSelectType>({
    organization: [],
    project: [],
    location: [],
    program: [],
    users: [],
  });
  const [lastSearchPayload, setLastSearchPayload] = useState<
    Partial<SearchPayloadType>
  >({});
  const [itemList, setItemList] = useState<any[]>([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 10 });
  const [selectedInvite, setSelectedInvite] = useState<any>(null);
  const [total, setTotal] = useState(0);
  const [showTimeline, setShowTimeline] = useState<boolean>(false);
  const [showModifyInvite, setShowModifyInvite] = useState<boolean>(false);
  const [inventoryColumnSearch, setInventoryColumnSearch] = useState<
    Record<string, string>
  >({}); 
  const filteredData = filterByColumnSearch(
    Array.isArray(itemList) ? itemList : [],
    inventoryColumnSearch,
  );

  const getTableData = useApi<any>(webservice_url.getApplicationTableData, {
    method: "POST",
    autoCall: false,
    handleGlobalError: false,
  });

  const resendInvitation = useApi<any>(webservice_url.sendInvitationAPI, {
    method: "PATCH",
    autoCall: false,
    handleGlobalError: false,
  });

  const getDaysElapsed = (invitationDate?: string) => {
    if (!invitationDate) return "-";
    const today = new Date();
    const invitedDate = new Date(invitationDate);
    today.setHours(0, 0, 0, 0);
    invitedDate.setHours(0, 0, 0, 0);
    const diffTime = today.getTime() - invitedDate.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays >= 0
      ? `${diffDays} Day${diffDays !== 1 ? "s" : ""}`
      : "0 Days";
  };

  const getExpireIn = (expiryDate?: string) => {
    if (!expiryDate) return "-";
    const today = new Date();
    const expiry = new Date(expiryDate);
    today.setHours(0, 0, 0, 0);
    expiry.setHours(0, 0, 0, 0);
    const diffTime = expiry.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return "Expired";
    return `${diffDays} Day${diffDays !== 1 ? "s" : ""}`;
  };

  const fetchTableData = async (
    searchPayload: Partial<SearchPayloadType>,
    page: number,
    limit: number,
  ) => {
    try {
      const apiPayload = buildTableApiPayload(
        searchPayload,
        page,
        limit,
        statusId,
      );
      const res = await getTableData.callApi(apiPayload);
      const rows: any[] = res?.data?.table ?? [];
      const paginationInfo = res?.data?.pagination;
      const mapped = rows.map((row: any, idx: number) => {
        const currentPage = paginationInfo?.page ?? page;
        const currentLimit = paginationInfo?.limit ?? limit;
        return {
          serial_number: row.serial_number,
          sno: (currentPage - 1) * currentLimit + idx + 1,
          assignment_id: row.assignment_id ?? row.org_case?.assignment_id,
          applicantName: row.candidate_name,
          caseId: row.case_id ?? row.org_case?.case_id,
          caseCode: row.case_code,
          caseSpoc: row.case_spoc_name,
          employeeId: row.employee_id,
          emailId: row.email,
          email: row.email,
          phone: row.phone,
          days_elapsed: getDaysElapsed(row.assigned_at ?? row.invitation_date),
          invitationDate: row.invitation_date ?? row.assigned_at,
          status: row.candidate_status,
          expires_at: getExpireIn(row.expires_at ?? row.invitation_expiry_date),
          department_name: row.department_name,
          program_name: row.program_name,
          project_name: row.project_name,
          overall_status: row.overall_status,
          overall_color_value: row.overall_color_value,
        };
      });
      setItemList(mapped);
      setTotal(paginationInfo?.total ?? mapped.length);
      setPagination({
        page: paginationInfo?.page ?? page,
        limit: paginationInfo?.limit ?? limit,
      });
    } catch (err) {
      console.error(err);
      setItemList([]);
      setTotal(0);
    }
  };

  useEffect(() => {
    fetchTableData({}, pagination.page, pagination.limit);
  }, [statusId]);

  const handleSearch = (payload: SearchPayloadType) => {
    setLastSearchPayload(payload);
    setPagination({ page: 1, limit: pagination.limit });
    fetchTableData(payload, 1, pagination.limit);
  };

  const [timelineData, setTimelineData] = useState<any[]>([]);
  const [timelineLoading, setTimelineLoading] = useState<boolean>(false);

  const getCaseLog = useApi<any>(webservice_url.ViewLogAPI, {
    method: "POST",
    autoCall: false,
    handleGlobalError: false,
  });

  const handleViewLog = async (caseId?: string) => {
    if (!caseId) return;
    setShowTimeline(true);
    setTimelineLoading(true);
    try {
      const res = await getCaseLog.callApi({ case_id: caseId });
      setTimelineData(res?.data ?? []);
    } catch (err) {
      console.error(err);
      setTimelineData([]);
    } finally {
      setTimelineLoading(false);
    }
  };
  const handleTableChange = (
    page: number,
    limit: number = pagination.limit,
  ) => {
    if (page === pagination.page && limit === pagination.limit) {
      return;
    }
    setPagination({ page, limit });
    fetchTableData(lastSearchPayload, page, limit);
  };

  const columns: ColumnsType<any> = [
    {
      title: "Sr. No.",
      key: "serial_number",
      width: 80,
      dataIndex: "serial_number",
      align: "center",
    },
    {
      title: getSearchableHeader(
        "Applicant Name",
        "applicantName",
        setInventoryColumnSearch,
      ),
      dataIndex: "applicantName",
      key: "applicantName",
      onCell: () => cellWidth(220),
      sorter: (a: any, b: any) =>
        a.applicantName.localeCompare(b.applicantName),
    },
    {
      title: getSearchableHeader("Case ID", "caseId", setInventoryColumnSearch),
      dataIndex: "caseId",
      key: "caseId",
      onCell: () => cellWidth(220),
      sorter: (a: any, b: any) => parseNumber(a.caseId) - parseNumber(b.caseId),
    },
    {
      title: getSearchableHeader(
        "Case SPOC",
        "caseSpoc",
        setInventoryColumnSearch,
      ),
      dataIndex: "caseSpoc",
      key: "caseSpoc",
      onCell: () => cellWidth(220),
      sorter: (a: any, b: any) => a.caseSpoc.localeCompare(b.caseSpoc),
    },
    {
      title: getSearchableHeader(
        "Case Status",
        "overall_status",
        setInventoryColumnSearch,
      ),
      dataIndex: "overall_status",
      key: "overall_status",
      onCell: () => cellWidth(220),
      sorter: (a: any, b: any) =>
        (a.overall_status ?? "").localeCompare(b.overall_status ?? ""),
      render: (text: any, record: any) => {
        return (
          <span
            style={{
              backgroundColor: record?.overall_color_value || "#F59E0B",
              color: "#fff",
              padding: "5px 10px",
              borderRadius: "4px",
              display: "inline-block",
              fontSize: "12px",
              fontWeight: 500,
              whiteSpace: "nowrap",
            }}
          >
            {text ?? "-"}
          </span>
        );
      },
    },
    {
      title: getSearchableHeader(
        "Candidate ID",
        "employeeId",
        setInventoryColumnSearch,
      ),
      dataIndex: "employeeId",
      key: "employeeId",
      onCell: () => cellWidth(220),
      sorter: (a: any, b: any) =>
        parseNumber(a.employeeId) - parseNumber(b.employeeId),
    },
    {
      title: getSearchableHeader(
        "Project",
        "project_name",
        setInventoryColumnSearch,
      ),
      dataIndex: "project_name",
      key: "project_name",
      onCell: () => cellWidth(220),
      width: "220px",
      sorter: (a: any, b: any) =>
        (a.project_name ?? "").localeCompare(b.project_name ?? ""),
    },
    {
      title: getSearchableHeader(
        "Program",
        "program_name",
        setInventoryColumnSearch,
      ),
      dataIndex: "program_name",
      key: "program_name",
      onCell: () => cellWidth(220),
      width: "220px",
      sorter: (a: any, b: any) =>
        (a.program_name ?? "").localeCompare(b.program_name ?? ""),
    },
    {
      title: getSearchableHeader(
        "Department",
        "department_name",
        setInventoryColumnSearch,
      ),
      dataIndex: "department_name",
      key: "department_name",
      onCell: () => cellWidth(220),
      width: "220px",
      sorter: (a: any, b: any) =>
        (a.department_name ?? "").localeCompare(b.department_name ?? ""),
    },
    {
      title: getSearchableHeader(
        "Email ID",
        "emailId",
        setInventoryColumnSearch,
      ),
      dataIndex: "emailId",
      key: "emailId",
      sorter: (a: any, b: any) => a.emailId.localeCompare(b.emailId),
    },
    {
      title: getSearchableHeader(
        "Days Elapsed",
        "days_elapsed",
        setInventoryColumnSearch,
      ),
      dataIndex: "days_elapsed",
      key: "days_elapsed",
      onCell: () => cellWidth(220),
      render: (value: string) => value || "-",
      sorter: (a: any, b: any) =>
        parseDaysString(a.days_elapsed) - parseDaysString(b.days_elapsed),
    },
    {
      title: getSearchableHeader(
        "Phone No.",
        "phone",
        setInventoryColumnSearch,
      ),
      dataIndex: "phone",
      key: "phone",
      onCell: () => cellWidth(220),
      render: (text: any) => <div>{text || "-"}</div>,
      sorter: (a: any, b: any) => parseNumber(a.phone) - parseNumber(b.phone),
    },
    {
      title: getSearchableHeader(
        "Invitation Date",
        "invitationDate",
        setInventoryColumnSearch,
      ),
      dataIndex: "invitationDate",
      render: (text: string) => {
        const date = text ? dayjs(text).format("DD-MM-YYYY") : "";
        return <>{date}</>;
      },
      key: "invitationDate",
      onCell: () => cellWidth(220),
      sorter: (a: any, b: any) =>
        parseDate(a.invitationDate).getTime() -
        parseDate(b.invitationDate).getTime(),
    },
    {
      title: getSearchableHeader("Status", "status", setInventoryColumnSearch),
      dataIndex: "status",
      key: "status",
      align: "center",
      onCell: () => cellWidth(180),
      render: (status: string) => (
        <span
          className={`badge ${statusColors[status?.toUpperCase()] || "bg-secondary"}`}
        >
          {status || "-"}
        </span>
      ),
      sorter: (a: any, b: any) => a.status.localeCompare(b.status),
    }, 
    {
      title: "Action",
      key: "action",
      onCell: () => cellWidth(160),
      render: (_: any, record: any) => (
        <button
          type="button"
          className="btn btn-sm btn-primary"
          onClick={() => {
            setSelectedInvite(record);
            setShowModifyInvite(true);
          }}
        >
          <EditIcon />
          &nbsp;Modify Invite
        </button>
      ),
    },
    {
      title: "View Log",
      key: "viewLog",
      onCell: () => cellWidth(120),
      render: (_: any, record: any) => (
        <div
          className="text-center text-dark"
          style={{ cursor: "pointer" }}
          onClick={() => handleViewLog(record.caseId)}
        >
          <i className="ti ti-eye "></i>
        </div>
      ),
    },
  ];

  const handleDownloadExcel = () => {
    exportAntdTableToExcel({
      columns,
      dataSource: filteredData,
      fileName: "In_Progress_Report",
      sheetName: "In Progress",
    });
  };

  const loading = getTableData.loading;
  const getApiErrorMessage = (err: any, fallback = "Something went wrong.") =>
    err?.response?.data?.error ||
    err?.response?.data?.message ||
    err?.error ||
    err?.message ||
    fallback;
  return (
    <>
      <FiltersTrackCase
        filterData={filterData}
        searchFilter={searchFilter}
        setFilterData={setFilterData}
        setSearchFilter={setSearchFilter}
        onSearch={handleSearch}
        storageKey="applicant_logged_in"
      />
      <div className="card mt-3 pt-2 pe-0">
        <div className="text-end px-2">
          <button
            className="btn btn-sm btn-primary"
            onClick={handleDownloadExcel}
            disabled={loading}
          >
            <i className="bi bi-download pe-2"></i>
            Export to Excel
          </button>
        </div>
        {loading ? (
          <Loading />
        ) : (
          <Datatable
            dataSource={filteredData}
            columns={columns}
            Selection={false}
            loading={getTableData.loading}
            rowKey="assignment_id"
            fixedHeader
            tableHeight={650}
            pagination={{
              current: pagination.page,
              pageSize: pagination.limit,
              total,
            }}
            handleTableChange={(pagination: any) => {
              handleTableChange(pagination.current, pagination.pageSize);
            }}
          />
        )}
      </div>

      {showTimeline && (
        <TimelineModal
          timelineData={timelineData}
          loading={timelineLoading}
          onClose={() => {
            setShowTimeline(false);
            setTimelineData([]);
          }}
        />
      )}
 
      {showModifyInvite && (
        <ModifyInviteModal
          handleClose={() => setShowModifyInvite(false)}
          applicant={selectedInvite}
          isSubmitting={resendInvitation.loading}
          handleSubmit={async (data: any) => {
            try {
              Swal.fire({
                title: "Processing...",
                text: "Please wait while we resend the invitation.",
                allowOutsideClick: false,
                allowEscapeKey: false,
                showConfirmButton: true,
                didOpen: () => {
                  Swal.showLoading();
                },
              });

              const payload = {
                case_id: Number(data.case_id ?? selectedInvite?.case_id),
                email_id: String(
                  data.email_id ?? data.email ?? selectedInvite?.email ?? "",
                ).trim(),
                phone_number: String(
                  data.phone_number ??
                    data.phone ??
                    selectedInvite?.phone ??
                    "",
                ).trim(),
              };

              const res = await resendInvitation.callApi(payload);

              const resData = res?.data;

              if (resData) {
                setItemList((prev) =>
                  prev.map((item) =>
                    String(item.case_id) === String(resData.case_id)
                      ? {
                          ...item,
                          email: payload.email_id,
                          phone: payload.phone_number,
                          invitation_date:
                            resData.invitation_sent_at ?? item.invitation_date,
                          days_elapsed: getDaysElapsed(
                            resData.invitation_sent_at,
                          ),
                          expires_at: getExpireIn(
                            resData.invitation_expires_at,
                          ),
                        }
                      : item,
                  ),
                );
              }

              setShowModifyInvite(false);
              await fetchTableData(
                lastSearchPayload,
                pagination.page,
                pagination.limit,
              );
              Swal.close();
              await Swal.fire({
                icon: "success",
                title: "Invite Sent Successfully",
                text: resData?.email_sent
                  ? "Invitation has been sent successfully."
                  : "Invite resent, but email delivery could not be confirmed.",
                confirmButtonColor: "#f26522",
                confirmButtonText: "OK",
              });
            } catch (error: any) {
              Swal.close();
              Swal.fire({
                icon: "error",
                title: "Failed",
                text: getApiErrorMessage(error),
                confirmButtonColor: "#f26522",
              });
            }
          }}
        />
      )}
    </>
  );
};

export default ApplicantLoggedIn;
